import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { EA_GEFAHREN, KBU_STATUS_OPTIONS, gefahrenOhneKbu } from "./gefahren";
import type { KbuStatus } from "./types";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "14px" },
  legend: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "8px",
    "@media (min-width: 720px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
  },
  legendItem: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
    fontSize: "0.78rem",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("8px"),
  },
  legendNum: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    fontSize: "0.72rem",
    fontWeight: 700,
    color: BRAND.white,
    backgroundColor: BRAND.ink,
    ...shorthands.borderRadius("999px"),
  },
  table: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    overflowX: "auto",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(200px, 1fr) 96px minmax(230px, auto)",
    alignItems: "center",
    columnGap: "12px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    ":last-child": { ...shorthands.borderBottom("0") },
  },
  headRow: {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
  },
  gefahrName: { fontSize: "0.9rem", color: BRAND.ink, fontWeight: 600 },
  dsi: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "0.72rem",
    color: BRAND.muted,
  },
  seg: { display: "flex", columnGap: "4px" },
  segBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.ink,
    whiteSpace: "nowrap",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("6px"),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
  },
  seg0: {
    backgroundColor: "#f0edf2",
    color: BRAND.ink,
    ...shorthands.borderColor("#c9c1cf"),
  },
  seg1: {
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    ...shorthands.borderColor(BRAND.gold),
  },
  seg2: {
    backgroundColor: BRAND.pillarGreen,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.pillarGreen),
  },
  autoBox: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    backgroundColor: "#fdf6e6",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
  },
  autoTitle: { fontSize: "0.86rem", fontWeight: 700, color: "#7a5a10" },
  autoList: { fontSize: "0.85rem", color: "#7a5a10", margin: 0, paddingLeft: "18px" },
  autoEmpty: { fontSize: "0.85rem", color: BRAND.muted },
});

type Props = {
  value: Record<string, KbuStatus>;
  onChange: (next: Record<string, KbuStatus>) => void;
};

/**
 * Abschnitt 4 der Felddefinition: KBU-Status je Gefahr (0 / 1 / 2) aus den
 * DSI-Feldern `*_O`. Darunter die automatische Liste „Gefahren ohne KBU“
 * (Excel-Zeile 36) — alle Gefahren mit Status 1.
 */
const GefahrenTabelle = ({ value, onChange }: Props) => {
  const styles = useStyles();
  const ohneKbu = gefahrenOhneKbu(value);

  return (
    <div className={styles.root}>
      <div className={styles.legend}>
        {KBU_STATUS_OPTIONS.map((o) => (
          <div key={o.value} className={styles.legendItem}>
            <span className={styles.legendNum}>{o.value}</span>
            <span>
              <strong>{o.label}</strong> — {o.hint}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.table}>
        <div className={mergeClasses(styles.row, styles.headRow)}>
          <span>Gefahr</span>
          <span>DSI-Feld</span>
          <span>KBU-Status</span>
        </div>
        {EA_GEFAHREN.map((g) => {
          const status = value[g.key] ?? "";
          return (
            <div key={g.key} className={styles.row}>
              <span className={styles.gefahrName}>{g.label}</span>
              <span className={styles.dsi}>{g.dsi}</span>
              <div className={styles.seg}>
                {KBU_STATUS_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    title={o.hint}
                    className={mergeClasses(
                      styles.segBtn,
                      status === o.value &&
                        (o.value === "0" ? styles.seg0 : o.value === "1" ? styles.seg1 : styles.seg2),
                    )}
                    onClick={() =>
                      onChange({ ...value, [g.key]: status === o.value ? "" : o.value })
                    }
                  >
                    {o.value} · {o.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.autoBox}>
        <span className={styles.autoTitle}>
          Gefahren ohne KBU — automatisch aus allen Gefahren mit Status 1
        </span>
        {ohneKbu.length ? (
          <ul className={styles.autoList}>
            {ohneKbu.map((g) => (
              <li key={g.key}>
                {g.label} <span className={styles.dsi}>({g.dsi})</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className={styles.autoEmpty}>
            Keine Gefahr mit Status 1 erfasst — es entsteht aktuell keine Abweichung aus dieser
            Liste.
          </span>
        )}
      </div>
    </div>
  );
};

export default GefahrenTabelle;
